import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Button,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Toggle,
  Select,
  Input,
  DataTable,
  Dropdown,
  ConfirmDialog,
} from '../../../design-system/components';
import {
  admin_fraud_root_path,
  admin_fraud_rule_path,
  new_admin_fraud_custom_rule_path,
  edit_admin_fraud_custom_rule_path,
  admin_fraud_custom_rule_path,
} from '../../../lib/routes';
const ACTIONS = ['flag', 'auto_reject', 'auto_deactivate'];
const actionVariant = {
  flag: 'warning',
  auto_reject: 'danger',
  auto_deactivate: 'danger',
};
const ruleTypeLabel = {
  self_referral: 'Self-Referral Detection',
  paid_traffic: 'Paid Traffic Detection',
  brand_bidding: 'Brand Bidding Detection',
  velocity_spike: 'Velocity Spike Detection',
  disposable_email: 'Disposable Email Detection',
  duplicate_payment_method: 'Duplicate Payment Method',
  ip_match: 'IP Match Detection',
};
const ruleTypeDescription = {
  self_referral: 'Detects affiliates who refer themselves or use their own referral link',
  paid_traffic: 'Identifies referrals from paid advertising channels (PPC, display ads)',
  brand_bidding: 'Detects affiliates bidding on brand keywords in search ads',
  velocity_spike: 'Flags unusual spikes in referral volume from a single affiliate',
  disposable_email: 'Blocks sign-ups using known disposable email providers',
  duplicate_payment_method: 'Detects multiple affiliates using the same payment method',
  ip_match: 'Flags referrals where the affiliate and customer share the same IP',
};
function BuiltInRuleCard({ rule }) {
  const [enabled, setEnabled] = useState(rule.enabled);
  const [action, setAction] = useState(rule.action);
  const [saving, setSaving] = useState(false);
  const config = rule.config || {};

  // Config state for specific rule types
  const [maxReferrals, setMaxReferrals] = useState(String(config.max_referrals_per_hour ?? 10));
  const [utmMediums, setUtmMediums] = useState(
    Array.isArray(config.utm_mediums) ? config.utm_mediums.join(', ') : '',
  );
  const [clickIdParams, setClickIdParams] = useState(
    Array.isArray(config.click_id_params) ? config.click_id_params.join(', ') : '',
  );
  const [adDomains, setAdDomains] = useState(
    Array.isArray(config.ad_domains) ? config.ad_domains.join(', ') : '',
  );
  function handleSave() {
    setSaving(true);
    const data = {
      'fraud_rule[enabled]': enabled ? '1' : '0',
      'fraud_rule[action]': action,
    };
    if (rule.rule_type === 'velocity_spike') {
      data['fraud_rule[max_referrals_per_hour]'] = maxReferrals;
    } else if (rule.rule_type === 'paid_traffic') {
      data['fraud_rule[utm_mediums]'] = utmMediums;
      data['fraud_rule[click_id_params]'] = clickIdParams;
    } else if (rule.rule_type === 'brand_bidding') {
      data['fraud_rule[ad_domains]'] = adDomains;
    }
    router.patch(admin_fraud_rule_path(rule.id), data, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
    });
  }
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Toggle checked={enabled} onChange={() => setEnabled(!enabled)} label="" />
          <div>
            <h3 className="text-sm font-semibold text-fg">
              {ruleTypeLabel[rule.rule_type] || rule.rule_type}
            </h3>
            <p className="text-xs text-fg-tertiary">
              {ruleTypeDescription[rule.rule_type] || ''}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <div className="space-y-4">
          <div className="max-w-xs">
            <Select label="Action" value={action} onChange={(e) => setAction(e.target.value)}>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a.replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
          </div>

          {rule.rule_type === 'velocity_spike' && (
            <div className="max-w-xs">
              <Input
                label="Max referrals per hour"
                type="number"
                value={maxReferrals}
                onChange={(e) => setMaxReferrals(e.target.value)}
                helperText="Flag affiliates exceeding this many referrals in one hour"
              />
            </div>
          )}

          {rule.rule_type === 'paid_traffic' && (
            <div className="space-y-3">
              <Input
                label="UTM Mediums"
                value={utmMediums}
                onChange={(e) => setUtmMediums(e.target.value)}
                helperText="Comma-separated list of UTM mediums to flag (e.g., cpc, ppc, display)"
              />
              <Input
                label="Click ID Parameters"
                value={clickIdParams}
                onChange={(e) => setClickIdParams(e.target.value)}
                helperText="Comma-separated URL params indicating paid clicks (e.g., gclid, fbclid, msclkid)"
              />
            </div>
          )}

          {rule.rule_type === 'brand_bidding' && (
            <Input
              label="Ad Domains"
              value={adDomains}
              onChange={(e) => setAdDomains(e.target.value)}
              helperText="Comma-separated list of ad network domains to detect (e.g., googleads.g.doubleclick.net)"
            />
          )}

          <div className="flex justify-end">
            <Button size="sm" onClick={handleSave} loading={saving}>
              Save Changes
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
export default function Rules({ built_in_rules, custom_rules }) {
  const [deletingId, setDeletingId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  function handleDelete() {
    if (deletingId === null) return;
    setDeleteLoading(true);
    router.delete(admin_fraud_custom_rule_path(deletingId), {
      preserveScroll: true,
      onFinish: () => {
        setDeleteLoading(false);
        setDeletingId(null);
      },
    });
  }
  const customColumns = [
    {
      id: 'name',
      header: 'Name',
      render: (row) => <span className="font-medium text-fg">{row.name}</span>,
    },
    {
      id: 'field',
      header: 'Field',
      render: (row) => {
        const config = row.config;
        return <span className="text-sm text-fg-secondary">{String(config.field || '—')}</span>;
      },
    },
    {
      id: 'operator',
      header: 'Operator',
      render: (row) => {
        const config = row.config;
        return (
          <Badge variant="default" size="sm">
            {String(config.operator || '—')}
          </Badge>
        );
      },
    },
    {
      id: 'value',
      header: 'Value',
      render: (row) => {
        const config = row.config;
        return <span className="text-sm text-fg-secondary">{String(config.value || '—')}</span>;
      },
    },
    {
      id: 'action',
      header: 'Action',
      render: (row) => (
        <Badge variant={actionVariant[row.action] ?? 'default'} size="sm">
          {row.action.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      id: 'enabled',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.enabled ? 'success' : 'default'} size="sm">
          {row.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      render: (row) => {
        const items = [
          {
            label: 'Edit',
            onClick: () => router.visit(edit_admin_fraud_custom_rule_path(row.id)),
          },
          {
            type: 'divider',
          },
          {
            label: 'Delete',
            onClick: () => setDeletingId(row.id),
            destructive: true,
          },
        ];
        return (
          <Dropdown
            trigger={
              <Button size="sm" variant="ghost">
                ...
              </Button>
            }
            items={items}
          />
        );
      },
    },
  ];
  return (
    <AdminLayout>
      <PageHeader
        title="Fraud Rules"
        description="Configure built-in and custom fraud detection rules"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.visit(admin_fraud_root_path())}
            >
              Back to Events
            </Button>
          </div>
        }
      />

      <div className="mt-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-fg mb-4">Built-in Rules</h2>
          <div className="space-y-4">
            {built_in_rules.map((rule) => (
              <BuiltInRuleCard key={rule.id} rule={rule} />
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-fg">Custom Rules</h2>
            <Button
              size="sm"
              onClick={() => router.visit(new_admin_fraud_custom_rule_path())}
            >
              New Custom Rule
            </Button>
          </div>

          <DataTable
            columns={customColumns}
            data={custom_rules}
            keyAccessor="id"
            emptyTitle="No custom rules"
            emptyDescription="Create custom rules to detect specific fraud patterns."
          />
        </div>
      </div>

      <ConfirmDialog
        open={deletingId !== null}
        onClose={() => setDeletingId(null)}
        onConfirm={handleDelete}
        title="Delete Custom Rule"
        message="Are you sure you want to delete this custom rule? This action cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={deleteLoading}
      />
    </AdminLayout>
  );
}
